import { useCallback, useEffect, useId, useRef } from "react";
import { X } from "lucide-react";
import { cn } from "../lib/cn.js";

interface Props {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

/**
 * One dialog, used by everything that needs one.
 *
 * The design this is ported from hand-rolled four separate overlays, and none
 * of the four had `role="dialog"`, `aria-modal`, a label, a focus trap, focus
 * restoration, an Escape handler or a named close button -- so once opened,
 * keyboard focus stayed on the page behind and the only way out was a mouse
 * click on an unlabelled X. Every one of them would fail the axe gate and the
 * keyboard requirement in docs/REQUIREMENTS.md section 4. Sharing one
 * component fixes all of it in one place, and means the next dialog inherits
 * the fix rather than repeating the omission.
 */
export function Modal({ title, onClose, children, className }: Props) {
  const headingId = useId();
  const panel = useRef<HTMLDivElement>(null);
  // Where focus was before the dialog opened. Restoring it is what stops a
  // keyboard reader being dropped at the top of the document every time they
  // close something.
  const opener = useRef<Element | null>(null);

  useEffect(() => {
    opener.current = document.activeElement;
    // Focus the panel rather than the first control: the first control here is
    // the close button, and opening a dialog with "Close" announced is a worse
    // introduction than the dialog's own name.
    panel.current?.focus();
    return () => {
      if (opener.current instanceof HTMLElement) opener.current.focus();
    };
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;

      // The trap. Without it Tab walks out of the dialog and into the page
      // behind, which is still rendered and still interactive.
      const focusable = panel.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusable === undefined || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  // Listened for on the document rather than bound to the panel. A handler on
  // the panel only fires while focus is inside it, and focus leaves as soon as
  // someone clicks the backdrop -- at which point Escape silently stopped
  // working and Tab walked into the page behind. The dialog is modal, so for
  // as long as it is open these keys are its own wherever focus sits.
  useEffect(() => {
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [onKeyDown]);

  return (
    // Presentational: it is a tint, and it owns no behaviour of its own. The
    // close it offers on click is a convenience that duplicates the Close
    // button and Escape, so a reader who never sees the backdrop loses nothing.
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm md:items-center md:p-6"
      // A click on the backdrop closes. The check keeps a click that started
      // inside the panel and drifted out from closing it.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className={cn(
          "max-h-[90dvh] w-full overflow-y-auto rounded-t-card border border-line bg-card p-6 shadow-2xl outline-none md:max-w-lg md:rounded-card",
          className,
        )}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={headingId} className="text-lg font-semibold text-ink">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="-mr-1 -mt-1 rounded-full p-2 text-ink-muted transition-colors hover:bg-line hover:text-ink"
          >
            {/* A name, not a `title`. `title` is not reliably surfaced as an
                accessible name and is not shown at all on touch. */}
            <span className="sr-only">Close</span>
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
